/**
 * List Component
 *
 * A flexible list component with the same styling as DataTable for consistency.
 * Use for non-tabular data that needs a clean, consistent list presentation.
 *
 * @example
 * ```jsx
 * // Basic usage
 * <List
 *   data={users}
 *   keyField="id"
 *   renderItem={(user) => (
 *     <div>
 *       <p className="font-medium">{user.name}</p>
 *       <p className="text-sm text-gray-500">{user.email}</p>
 *     </div>
 *   )}
 * />
 *
 * // With header and actions
 * <List
 *   data={notifications}
 *   header="Recent Notifications"
 *   renderItem={(item) => <NotificationItem {...item} />}
 *   onItemClick={(item) => markAsRead(item.id)}
 *   emptyTitle="No notifications"
 * />
 * ```
 */

import { InboxIcon } from '@heroicons/react/24/outline';

const classNames = (...classes) => classes.filter(Boolean).join(' ');

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Loading skeleton items
 */
const ListSkeleton = ({ count = 5, skeletonHeight = 'h-12' }) => (
	<>
		{[...Array(count)].map((_, i) => (
			<div
				key={i}
				className="px-4 py-4 border-b border-gray-50 last:border-b-0 animate-pulse"
			>
				<div className="flex items-center gap-4">
					<div className="h-10 w-10 bg-gray-100 rounded-full flex-shrink-0" />
					<div className="flex-1">
						<div className="h-4 bg-gray-100 rounded w-1/3 mb-2" />
						<div className="h-3 bg-gray-50 rounded w-1/2" />
					</div>
				</div>
			</div>
		))}
	</>
);

/**
 * Empty state display
 */
const EmptyState = ({
	icon: Icon = InboxIcon,
	title = 'No items found',
	description = 'There are no items to display',
}) => (
	<div className="px-6 py-16">
		<div className="flex flex-col items-center text-center">
			<Icon className="w-12 h-12 text-gray-300 mb-4" />
			<p className="text-sm font-medium text-gray-900 mb-1">{title}</p>
			<p className="text-sm text-gray-500">{description}</p>
		</div>
	</div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const List = ({
	// Data
	data = [],
	keyField = 'id',
	loading = false,

	// Rendering
	renderItem,
	header,
	footer,

	// Events
	onItemClick,

	// Styling
	className = '',
	containerClassName = '',
	itemClassName = '',
	headerClassName = '',
	bordered = true,
	dividers = true,
	hoverable = true,
	compact = false,

	// Empty/Loading
	emptyIcon,
	emptyTitle,
	emptyDescription,
	skeletonCount = 5,

	// Actions
	actions = null,
}) => {
	if (!renderItem) {
		console.warn('List: renderItem prop is required');
		return null;
	}

	return (
		<div
			className={classNames(
				'bg-white rounded-lg',
				bordered ? 'border border-gray-200' : '',
				containerClassName,
			)}
		>
			{/* Header */}
			{(header || actions) && (
				<div
					className={classNames(
						'flex items-center justify-between px-4',
						compact ? 'py-3' : 'py-4',
						dividers ? 'border-b border-gray-100' : '',
						headerClassName,
					)}
				>
					{header && (
						<h3 className="text-sm font-medium text-gray-900">{header}</h3>
					)}
					{actions && <div className="flex items-center gap-2">{actions}</div>}
				</div>
			)}

			{/* List Content */}
			<div className={className}>
				{loading ? (
					<ListSkeleton count={skeletonCount} />
				) : data.length === 0 ? (
					<EmptyState
						icon={emptyIcon}
						title={emptyTitle}
						description={emptyDescription}
					/>
				) : (
					data.map((item, index) => {
						const itemKey = item[keyField] ?? index;

						return (
							<div
								key={itemKey}
								onClick={onItemClick ? () => onItemClick(item) : undefined}
								className={classNames(
									'transition-colors',
									compact ? 'px-4 py-2' : 'px-4 py-4',
									dividers && index < data.length - 1
										? 'border-b border-gray-50'
										: '',
									onItemClick ? 'cursor-pointer' : '',
									hoverable ? 'hover:bg-gray-50/50' : '',
									typeof itemClassName === 'function'
										? itemClassName(item, index)
										: itemClassName,
								)}
							>
								{renderItem(item, index)}
							</div>
						);
					})
				)}
			</div>

			{/* Footer */}
			{footer && (
				<div
					className={classNames(
						'px-4',
						compact ? 'py-3' : 'py-4',
						dividers ? 'border-t border-gray-100' : '',
					)}
				>
					{footer}
				</div>
			)}
		</div>
	);
};

// ============================================================================
// LIST ITEM HELPERS
// ============================================================================

/**
 * ListItem - A pre-styled list item component for common patterns
 */
export const ListItem = ({
	// Content
	title,
	description,
	icon: Icon,
	avatar,
	badge,

	// Actions
	actions,
	onClick,

	// Styling
	className = '',
}) => (
	<div
		onClick={onClick}
		className={classNames(
			'flex items-center gap-4',
			onClick ? 'cursor-pointer' : '',
			className,
		)}
	>
		{/* Icon or Avatar */}
		{Icon && (
			<div className="flex-shrink-0">
				<Icon className="w-5 h-5 text-gray-400" />
			</div>
		)}
		{avatar && <div className="flex-shrink-0">{avatar}</div>}

		{/* Content */}
		<div className="flex-1 min-w-0">
			{title && (
				<p className="text-sm font-medium text-gray-900 truncate">{title}</p>
			)}
			{description && (
				<p className="text-sm text-gray-500 truncate">{description}</p>
			)}
		</div>

		{/* Badge */}
		{badge && <div className="flex-shrink-0">{badge}</div>}

		{/* Actions */}
		{actions && (
			<div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
				{actions}
			</div>
		)}
	</div>
);

/**
 * ListItemSkeleton - Loading skeleton for ListItem
 */
export const ListItemSkeleton = ({ showIcon = true, showBadge = false }) => (
	<div className="flex items-center gap-4 animate-pulse">
		{showIcon && (
			<div className="h-10 w-10 bg-gray-100 rounded-full flex-shrink-0" />
		)}
		<div className="flex-1">
			<div className="h-4 bg-gray-100 rounded w-1/3 mb-2" />
			<div className="h-3 bg-gray-50 rounded w-1/2" />
		</div>
		{showBadge && (
			<div className="h-6 w-16 bg-gray-100 rounded-full flex-shrink-0" />
		)}
	</div>
);

export default List;
